// Extracted from CaseStudyInstructionSystem.jsx to reduce component complexity.
// Bilingual content (DE/EN) for the Digital Instruction System case study page.

export const seoContent = {
  de: {
    title: 'Case Study Unterweisungssystem - Digitale Pflichtschulungen',
    description: 'Praxisbeispiel: Wie ein Unternehmen mit mehreren hundert Mitarbeitern Pflichtschulungen digitalisiert hat. Von Präsenz zu E-Learning. Zeitersparnis, Dokumentation, Compliance.',
    keywords: 'Case Study Unterweisungssystem, Pflichtschulung digitalisieren, E-Learning Praxisbeispiel, Arbeitssicherheit digitalisieren, Compliance Schulung Beispiel, Unterweisungssystem Erfolgsgeschichte'
  },
  en: {
    title: 'Case Study Training System - Digital Mandatory Training',
    description: 'Practical example: How a company with several hundred employees digitized mandatory training. From in-person to e-learning. Time savings, documentation, compliance.',
    keywords: 'Case Study Training System, digitize mandatory training, E-Learning practical example, digitize workplace safety, compliance training example, training system success story'
  }
};
export const content = {
  de: {
    title: 'Case Study',
    subtitle: 'Digitale Schulungen für verpflichtende Unterweisungen',
    backLink: 'Zurück zum Portfolio',
    
    caseStudy: {
      situation: {
        title: 'Ausgangssituation',
        text: 'Ein Unternehmen mit mehreren hundert Mitarbeitern musste regelmäßig verpflichtende Schulungen in den Bereichen Arbeitssicherheit und Compliance durchführen. Die Schulungen wurden überwiegend als Präsenzveranstaltungen organisiert. Dies führte zu einem hohen organisatorischen Aufwand:',
        points: [
          'Schulungstermine mussten geplant werden',
          'Mitarbeiter mussten gleichzeitig verfügbar sein',
          'Teilnahme musste manuell dokumentiert werden',
          'Fehlende Schulungen mussten nachverfolgt werden'
        ],
        conclusion: 'Gerade bei mehreren Standorten wurde die Organisation zunehmend aufwendig.'
      },
      
      challenge: {
        title: 'Herausforderung',
        text: 'Das Unternehmen suchte eine Lösung, um:',
        points: [
          'Gesetzliche Schulungspflichten zuverlässig zu erfüllen',
          'Den organisatorischen Aufwand deutlich zu reduzieren',
          'Schulungen digital zu dokumentieren',
          'Jederzeit einen Überblick über den Schulungsstand zu haben'
        ]
      },
      
      solution: {
        title: 'Lösung',
        text: 'Mit dem digitalen Unterweisungssystem von e-Matrix wurden die verpflichtenden Schulungen vollständig digital umgesetzt. Die Mitarbeiter absolvieren die Schulungen als e-Training über eine zentrale Plattform.',
        subtitle: 'Das System übernimmt automatisch:',
        points: [
          'Kurszuweisungen',
          'Schulungsdokumentation',
          'Erinnerungen an offene Schulungen',
          'Auswertungen für Audits und Kontrollen'
        ],
        conclusion: 'Die Schulungen können zeit- und ortsunabhängig absolviert werden.'
      },
      
      result: {
        title: 'Ergebnis',
        text: 'Nach der Einführung der digitalen Schulungen konnte das Unternehmen:',
        points: [
          'Den organisatorischen Aufwand deutlich reduzieren',
          'Schulungsstände jederzeit transparent einsehen',
          'Schulungen revisionssicher dokumentieren',
          'Mitarbeiter flexibel schulen – ohne Terminkoordination'
        ]
      },
      
      comparison: {
        title: 'Vorher / Nachher',
        before: {
          title: 'Vorher',
          points: [
            'Präsenzschulungen',
            'Hoher organisatorischer Aufwand',
            'Keine zentrale Übersicht',
            'Manuelle Dokumentation',
            'Terminkoordination notwendig'
          ]
        },
        after: {
          title: 'Nachher',
          points: [
            'Digitale Schulungen',
            'Automatische Dokumentation',
            'Klare Übersicht über alle Schulungen',
            'Flexible Durchführung',
            'Audit-sichere Nachweise'
          ]
        }
      }
    },
    
    cta: {
      title: 'Ähnliche Herausforderungen?',
      text: 'Kontaktieren Sie uns für eine individuelle Beratung und erfahren Sie, wie wir auch Ihr Unternehmen unterstützen können.',
      button: 'Kontakt aufnehmen',
      systemLink: 'Mehr zum Unterweisungssystem'
    }
  },
  en: {
    title: 'Case Study',
    subtitle: 'Digital Training for Mandatory Instructions',
    backLink: 'Back to Portfolio',
    
    caseStudy: {
      situation: {
        title: 'Initial Situation',
        text: 'A company with several hundred employees had to regularly conduct mandatory training in the areas of occupational safety and compliance. The training was predominantly organized as in-person events. This led to high organizational effort:',
        points: [
          'Training dates had to be scheduled',
          'Employees had to be available at the same time',
          'Attendance had to be documented manually',
          'Missing training had to be tracked'
        ],
        conclusion: 'Especially with multiple locations, the organization became increasingly complex.'
      },
      
      challenge: {
        title: 'Challenge',
        text: 'The company was looking for a solution to:',
        points: [
          'Reliably fulfill legal training obligations',
          'Significantly reduce organizational effort',
          'Document training digitally',
          'Have an overview of training status at all times'
        ]
      },
      
      solution: {
        title: 'Solution',
        text: 'With the digital instruction system from e-Matrix, mandatory training was fully implemented digitally. Employees complete the training as e-training via a central platform.',
        subtitle: 'The system automatically handles:',
        points: [
          'Course assignments',
          'Training documentation',
          'Reminders for pending training',
          'Reports for audits and controls'
        ],
        conclusion: 'Training can be completed regardless of time and location.'
      },
      
      result: {
        title: 'Result',
        text: 'After introducing digital training, the company was able to:',
        points: [
          'Significantly reduce organizational effort',
          'View training status transparently at any time',
          'Document training in an audit-proof manner',
          'Train employees flexibly – without scheduling coordination'
        ]
      },
      
      comparison: {
        title: 'Before / After',
        before: {
          title: 'Before',
          points: [
            'In-person training',
            'High organizational effort',
            'No central overview',
            'Manual documentation',
            'Scheduling coordination required'
          ]
        },
        after: {
          title: 'After',
          points: [
            'Digital training',
            'Automatic documentation',
            'Clear overview of all training',
            'Flexible implementation',
            'Audit-proof records'
          ]
        }
      }
    },
    
    cta: {
      title: 'Facing Similar Challenges?',
      text: 'Contact us for individual consultation and learn how we can support your company too.',
      button: 'Contact Us',
      systemLink: 'Learn more about the Instruction System'
    }
  }
};

